/**
 * 유사 문제 카드 컨테이너 — 슬롯 atom 을 구독해 자기 카드의 슬롯이 running/error 일 때 그린다.
 * 생성 중 카드는 실패해도 사라지지 않고 같은 자리에서 실패 카드로 바뀐다.
 * 폼 아이템이 아니라 UI 전용이라 유효성·저장에 영향이 없다.
 */
import { useWatch } from 'react-hook-form';
import { useAtomValue, useSetAtom } from 'jotai';
import '../utils/events.quiz-question-card-ai';
import { trackEvent } from '@zep/logger/tracking';
import { useBuildFormContext } from '../../../form/schema';
import { useQuestionCardAiActions } from '../hooks/useQuestionCardAiActions';
import { useQuestionCardAiCopy } from '../hooks/useQuestionCardAiCopy';
import { useQuizDisplayNumber } from '../hooks/useQuizDisplayNumber';
import { dropSlot, type QuestionCardAiSlot, similarSlotAtom } from '../stores/questionCardAi.atoms';
import { QUESTION_CARD_AI_ACTION_SPECS } from '../utils/questionCardAiActionSpecs';
import { eligibilityReasonMessage } from '../utils/questionCardAiErrorMessage';
import { fillSlotVariantOf } from '../utils/questionCardAiSlotSchema';
import { SimilarQuestionPendingCardUI } from './SimilarQuestionPendingCardUI';
type PendingCardProps = {
  quizId: string;
  slot: QuestionCardAiSlot;
  sourceIndex: number;
  count: number;
};

/** 슬롯이 있을 때만 마운트된다 — 원본 구독·runner 훅을 카드마다 상시 돌리지 않는다. */
const PendingCard = ({
  quizId,
  slot,
  sourceIndex,
  count
}: PendingCardProps) => {
  const copy = useQuestionCardAiCopy();
  const setSlots = useSetAtom(similarSlotAtom);
  const {
    cancel,
    retry,
    isLocked
  } = useQuestionCardAiActions({
    quizId
  });
  const {
    control
  } = useBuildFormContext();
  // 실패 카드가 떠 있는 동안 원본을 고치면(문제 비움·이미지 추가) 재시도 자격도 바뀐다.
  // runner 는 자격 미달 재시도를 조용히 슬롯만 닫으므로, 버튼이 먼저 그 상태를 보여야 한다.
  const source = useWatch({
    control,
    name: `quizList.${sourceIndex}`
  });
  // 원본 바로 아래 자리 — 원본 번호(위쪽 대기 카드만큼 밀린 값) 다음 번호다.
  const number = useQuizDisplayNumber(sourceIndex) + 1;
  const action = slot.action ?? 'similar';
  const eligibility = QUESTION_CARD_AI_ACTION_SPECS[action].eligibility({
    quiz: source,
    count
  });
  const retryBlockedReason = isLocked ? copy.reason.running : eligibility.enabled ? undefined : eligibilityReasonMessage(copy, eligibility.reason, fillSlotVariantOf(source.type));
  return <SimilarQuestionPendingCardUI quizId={quizId} status={slot.status} number={number} type={source.type} onDelete={slot.status === 'running' ? cancel : () => {
    trackEvent('quiz_question_card_ai_failure_card_close', {
      action,
      quiz_type: source.type,
      error_code: slot.code ?? ''
    });
    setSlots(prev => dropSlot(prev, quizId));
  }} onRetry={() => retry(action)} retryBlockedReason={retryBlockedReason} data-sentry-element="SimilarQuestionPendingCardUI" data-sentry-component="PendingCard" data-sentry-source-file="SimilarQuestionPendingCard.tsx" />;
};
export const SimilarQuestionPendingCard = ({
  quizId
}: {
  quizId: string;
}) => {
  const slot = useAtomValue(similarSlotAtom)[quizId];
  const {
    getValues
  } = useBuildFormContext();
  if (slot == null) return null;
  const list = getValues('quizList');
  const sourceIndex = list.findIndex(quiz => quiz.id === quizId);
  // 원본이 사라졌으면 러너가 결과를 폐기하며 슬롯을 닫는다 — 그 사이엔 그리지 않는다.
  if (sourceIndex === -1) return null;
  return <PendingCard quizId={quizId} slot={slot} sourceIndex={sourceIndex} count={list.length} data-sentry-element="PendingCard" data-sentry-component="SimilarQuestionPendingCard" data-sentry-source-file="SimilarQuestionPendingCard.tsx" />;
};